import clsx from "clsx";
import { saveAs } from "file-saver";
import {
  BriefcaseBusinessIcon,
  CalendarDaysIcon,
  DownloadIcon,
  EllipsisIcon,
  Trash2Icon,
  UsersIcon,
} from "lucide-react";

import { JobFileViewerDialog } from "./JobFileViewerDialog";
import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useSelectedResourceIdFromUrl } from "@/hooks/useSelectedResourceIdFromUrl";
import { useDeleteJobFile } from "@/hooks/mutations/useDeleteJobFile";
import { useGetJobFile } from "@/hooks/queries/useGetJobFile";
import { useGetUser } from "@/hooks/queries/useGetUser";
import { useRemoveJobFile } from "@/hooks/sets/useRemoveJobFile";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { JobFile } from "@/types";

// TODO: STF-396: Factor out into reusable component.

const CreatedBy = ({ userId }: { userId: number }) => {
  const { data: user, status } = useGetUser({ userId });

  if (status === "pending") {
    // TODO: Loading state.
    return null;
  } else if (status === "error") {
    // TODO: Error state.
    return null;
  }

  return (
    <Text>
      {user.firstName} {user.lastName}
    </Text>
  );
};

const DropdownActions = ({ jobFileId }: { jobFileId: number }) => {
  const { mutate: deleteJobFile } = useDeleteJobFile();
  const { refetch } = useGetJobFile({
    enabled: false,
    jobFileId,
  });
  const removeJobFile = useRemoveJobFile();

  const handleDeleteJobFile = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent the row from being clicked.
    e.stopPropagation();

    mixpanelUtils.trackButtonClick("Delete file", MIXPANEL_SCREENS.JOB);
    deleteJobFile(
      { jobFileId },
      {
        onSuccess: () => {
          removeJobFile({ jobFileId });
        },
      },
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost">
          <EllipsisIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async (e) => {
            e.stopPropagation();
            mixpanelUtils.trackButtonClick("Download file", MIXPANEL_SCREENS.JOB);
            const { data } = await refetch();
            if (!data) return;
            saveAs(data.signedUrl, data.name);
          }}
        >
          <DownloadIcon className="h-4 w-4" />
          Download
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-red-500 hover:!text-red-500"
          onClick={handleDeleteJobFile}
        >
          <Trash2Icon className="h-4 w-4" />
          Delete file
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const JobFilesTable = ({
  className,
  jobFiles,
}: {
  className?: string;
  jobFiles: JobFile[];
}) => {
  const {
    selectedResourceId: selectedJobFileId,
    setSelectedResourceId: setSelectedJobFileId,
  } = useSelectedResourceIdFromUrl({
    paramName: "f",
  });

  // Find the selected file based on the ID from URL.
  const selectedJobFile = selectedJobFileId
    ? (jobFiles.find((file) => file.id === selectedJobFileId) ?? null)
    : null;

  return (
    <Column className={clsx("overflow-auto", className)}>
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[52%]">
                <Row className="items-center gap-1">
                  <BriefcaseBusinessIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">File Name</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[20%]">
                <Row className="items-center gap-1">
                  <UsersIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Created By</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[20%]">
                <Row className="items-center gap-1">
                  <CalendarDaysIcon className="h-4 w-4 text-slate-700" />
                  <Text variant="header">Creation Date</Text>
                </Row>
              </TableHead>
              <TableHead className="w-[8%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table>
          <TableBody>
            {jobFiles.map((jobFile: JobFile) => {
              return (
                <TableRow
                  key={jobFile.id}
                  onClick={() => {
                    mixpanelUtils.trackRowClick("Job file", MIXPANEL_SCREENS.JOB);
                    setSelectedJobFileId(jobFile.id);
                  }}
                >
                  <TableCell className="w-[52%]">
                    <Text>{jobFile.name}</Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <CreatedBy userId={jobFile.userId} />
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text>
                      {new Intl.DateTimeFormat("en-US").format(
                        new Date(jobFile.createdAt),
                      )}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[8%]">
                    <DropdownActions jobFileId={jobFile.id} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <JobFileViewerDialog
        jobFileId={selectedJobFile?.id}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedJobFileId(null);
          }
        }}
        open={!!selectedJobFileId}
      />
    </Column>
  );
};
